import { logger } from '@/lib/logger';
import { clearPostAuthIntent } from '@/lib/auth-intent';
import { clearLastVisitedPage } from '@/lib/last-visited-page';
import { clearPendingInvite } from '@/lib/pending-invite';
import { getAllStoredClickIds } from '@/lib/marketing/utm-builder';
import { isGpcEnabled } from '@/lib/analytics/openai-ads';
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import {
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type User as FirebaseUser
} from 'firebase/auth';
import { firebaseAuth } from './firebase';
import { AI_BASE_URL } from '@/lib/ai-base-url';
import { authLog } from '@/lib/debug-logger';
import { queryClient } from '@/lib/query-client';
import { supabase } from '@/lib/supabase';

// Compatible user shape — maps Firebase uid -> id for backward compat
// Consumers access user.id and user.email extensively across the codebase
export interface AuthUser {
  id: string;
  email: string | null;
  emailVerified: boolean;
  user_metadata?: {
    full_name?: string;
    first_name?: string;
    last_name?: string;
    is_admin?: boolean;
  };
}

// Compatible session shape — provides access_token for consumers
// Used by useEssayCoach, use-score-import, useFrillSSO, use-query-threads
export interface AuthSession {
  access_token: string;
  user: AuthUser;
}

// Define the shape of our auth context
export interface AuthContextType {
  user: AuthUser | null;
  session: AuthSession | null;
  signIn: (email: string, password: string) => Promise<AuthResult>;
  signUp: (email: string, password: string) => Promise<AuthResult>;
  signOut: () => Promise<void>;
  loading: boolean;
  isAuthResolved: boolean;
  // Admin "view as user" impersonation — driven purely by the Firebase custom
  // claim { impersonation: true, impersonated_by: <adminUid> } minted server-side.
  // The claim is the source of truth (survives refresh); we never persist this
  // to storage. See useImpersonation + ImpersonationBanner.
  isImpersonating: boolean;
  impersonatedBy: string | null;
}

// Define the result type for auth operations
export interface AuthResult {
  success: boolean;
  error?: string;
  data?: unknown;
}

// Create the context with a default value
export const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  signIn: async () => ({ success: false, error: 'Auth context not initialized' }),
  signUp: async () => ({ success: false, error: 'Auth context not initialized' }),
  signOut: async () => {},
  loading: true,
  isAuthResolved: false,
  isImpersonating: false,
  impersonatedBy: null
});

/**
 * Map a Firebase User to our compatible AuthUser shape.
 * Firebase uses `uid`; our codebase uses `id` everywhere.
 */
async function toAuthUser(firebaseUser: FirebaseUser): Promise<AuthUser> {
  // Get custom claims (includes is_admin) from token result
  const tokenResult = await firebaseUser.getIdTokenResult();
  const claims = tokenResult.claims;

  // Parse display name into first/last
  const displayName = firebaseUser.displayName || '';
  const nameParts = displayName.split(' ');

  return {
    id: firebaseUser.uid,
    email: firebaseUser.email,
    emailVerified: firebaseUser.emailVerified,
    user_metadata: {
      full_name: displayName || undefined,
      first_name: nameParts[0] || undefined,
      last_name: nameParts.slice(1).join(' ') || undefined,
      is_admin: claims.is_admin === true || claims.admin === true,
    },
  };
}

/**
 * Fire-and-forget login event to user_logins table.
 *
 * Observability: full PostgREST error objects (status/code/message/details/hint)
 * are logged via console.error so silent RLS or FK failures are visible in the
 * browser console. Errors are NOT rethrown — login should never block auth flow.
 *
 * Retry: on auth-class failures (PGRST301 expired JWT, 401/403) the insert is
 * retried up to 3 times with 500ms/1s/2s backoff. This tolerates a brief race
 * between Firebase `onIdTokenChanged` firing and the Firebase JWKS being ready
 * at the PostgREST edge (e.g. cold-start or token-refresh windows).
 */
function trackLogin(userId: string, loginType: 'sign_in' | 'session_restore') {
  const BACKOFF_MS = [500, 1000, 2000];

  const attempt = async (retryIdx: number): Promise<void> => {
    try {
      const { error, status } = await supabase.from('user_logins').insert({
        user_id: userId,
        login_type: loginType,
        device_info: {
          userAgent: navigator.userAgent,
          screenWidth: window.innerWidth,
        },
      });

      if (!error) {
        if (retryIdx > 0) {
          authLog.info('[trackLogin] succeeded after retry', { userId, loginType, retryIdx });
        }
        return;
      }

      // Full PostgREST error payload — previously swallowed as "Login tracking failed: <msg>"
      const errorPayload = {
        userId,
        loginType,
        retryIdx,
        status,
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint,
      };

      // Auth-class errors: retry with backoff (Firebase JWT may be mid-refresh)
      const isAuthError =
        error.code === 'PGRST301' || // JWT expired / missing
        status === 401 ||
        status === 403;

      if (isAuthError && retryIdx < BACKOFF_MS.length) {
        authLog.warn('[trackLogin] auth error, retrying', errorPayload);
        await new Promise(r => setTimeout(r, BACKOFF_MS[retryIdx]));
        return attempt(retryIdx + 1);
      }

      // Terminal failure — log loudly so we can diagnose vs. silently losing data
      // eslint-disable-next-line no-console
      console.error('[trackLogin] INSERT FAILED', errorPayload);
      authLog.error('[trackLogin] INSERT FAILED', errorPayload);
    } catch (err) {
      // Network-level failure (fetch abort, offline, etc.) — also previously swallowed
      // eslint-disable-next-line no-console
      console.error('[trackLogin] NETWORK FAILURE', { userId, loginType, retryIdx, err });
      authLog.error('[trackLogin] NETWORK FAILURE', { userId, loginType, retryIdx, err });
    }
  };

  // Fire and forget — never block auth flow
  attempt(0);
}

// Create the provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const hasLoggedInit = useRef(false);
  const authListenerSetup = useRef(false);
  const signInTracked = useRef(false); // prevents double-tracking when signIn() fires before onIdTokenChanged

  if (!hasLoggedInit.current) {
    authLog.info('AuthProvider initializing');
    hasLoggedInit.current = true;
  }

  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthResolved, setIsAuthResolved] = useState(false);
  // Impersonation state, derived from the Firebase custom claim on each token change.
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedBy, setImpersonatedBy] = useState<string | null>(null);

  // Track previous user to detect user changes (vs token refreshes)
  const previousUserId = useRef<string | null>(null);

  // Firebase auth state listener
  // Uses onIdTokenChanged (not onAuthStateChanged) to also capture token refreshes,
  // keeping session.access_token fresh for consumers that read it from context.
  useEffect(() => {
    if (authListenerSetup.current) return;
    authListenerSetup.current = true;

    const unsubscribe = onIdTokenChanged(firebaseAuth, async (firebaseUser) => {
      if (firebaseUser) {
        const authUser = await toAuthUser(firebaseUser);

        // Read impersonation claims from the token (getIdTokenResult is cached —
        // this does not force a network refresh). The claim is the single source
        // of truth for whether this is an admin "view as user" session.
        try {
          const tokenResult = await firebaseUser.getIdTokenResult();
          const impersonating = tokenResult.claims.impersonation === true;
          setIsImpersonating(impersonating);
          setImpersonatedBy(
            impersonating ? ((tokenResult.claims.impersonated_by as string) ?? null) : null
          );
        } catch {
          setIsImpersonating(false);
          setImpersonatedBy(null);
        }

        // If user changed (not just token refresh), clear caches
        if (previousUserId.current && previousUserId.current !== authUser.id) {
          authLog.info('User changed, clearing React Query cache and realtime subscriptions', {
            previousUserId: previousUserId.current,
            newUserId: authUser.id
          });
          queryClient.clear();
          // Realtime reconnection removed — polling handles updates
        }

        // Get fresh token for the session shim
        try {
          const token = await firebaseUser.getIdToken();
          setSession({ access_token: token, user: authUser });
        } catch (err) {
          authLog.error('Failed to get ID token:', err);
          setSession(null);
        }

        // Track session restore: first auth callback with a cached user (not from signIn)
        const isFirstAuth = !previousUserId.current;
        setUser(authUser);
        previousUserId.current = authUser.id;

        if (isFirstAuth && !signInTracked.current) {
          trackLogin(authUser.id, 'session_restore');
        }
        signInTracked.current = false;

        // Only log on initial auth, not every token refresh
        if (isFirstAuth) {
          authLog.info('User authenticated', { userId: authUser.id });
        }
      } else {
        // User signed out
        if (previousUserId.current) {
          authLog.info('User signed out, clearing caches');
          queryClient.clear();
          // Realtime reconnection removed — polling handles updates
        }
        setUser(null);
        setSession(null);
        setIsImpersonating(false);
        setImpersonatedBy(null);
        previousUserId.current = null;
        authLog.info('No authenticated user');
      }

      setLoading(false);
      setIsAuthResolved(true);
    });

    // Refresh Firebase token when app resumes from background (Capacitor native)
    // This prevents expired token errors after the app has been backgrounded
    const handleAppResume = async () => {
      const currentUser = firebaseAuth.currentUser;
      if (currentUser) {
        try {
          await currentUser.getIdToken(true); // force refresh
          authLog.debug('Token refreshed on app resume');
        } catch (err) {
          authLog.warn('Token refresh on app resume failed:', err);
        }
      }
    };
    window.addEventListener('capacitor:appResumed', handleAppResume);

    // Failsafe timeout to ensure auth always resolves (e.g. if Firebase SDK hangs)
    const authResolveTimeout = setTimeout(() => {
      setIsAuthResolved((current) => {
        if (!current) {
          authLog.warn('Auth resolution timeout - forcing resolved state');
          setLoading(false);
          return true;
        }
        return current;
      });
    }, 5000);

    return () => {
      unsubscribe();
      authListenerSetup.current = false;
      clearTimeout(authResolveTimeout);
      window.removeEventListener('capacitor:appResumed', handleAppResume);
    };
  }, []);

  // Sign in with email and password
  const signIn = useCallback(async (email: string, password: string): Promise<AuthResult> => {
    authLog.info('Sign in attempt', { email });
    setLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(firebaseAuth, email, password);
      signInTracked.current = true;
      trackLogin(credential.user.uid, 'sign_in');
      return { success: true, data: { user: toAuthUser(credential.user) } };
    } catch (err: unknown) {
      const error = err as { message: string; code?: string };

      // Map Firebase error codes to user-friendly messages
      let errorMessage = error.message;
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled. Please contact support.';
      }

      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign up with email and password (server-side user creation)
  const signUp = useCallback(async (email: string, password: string): Promise<AuthResult> => {
    setLoading(true);
    try {
      const paymentBaseUrl = import.meta.env.VITE_PAYMENT_API_URL || AI_BASE_URL;

      // 1. Create user server-side (Firebase user with UUID + Postgres rows)
      //
      // `gpc` rides the SIGNUP request, not the post-signup attribution POST.
      // This flow creates the user server-side and performs NO client-side
      // Firebase sign-in, so firebaseAuth.currentUser is null immediately
      // after — there is no bearer token to prove identity on a later call,
      // and /pay/attribution downgrades an unauthenticated gpc:true (drops the
      // opt-out, keeps the row). Sending it here is the only moment a
      // brand-new user's Global Privacy Control signal can be honored: the
      // server sets profiles.ads_opt_out inside the create-profile
      // transaction, where identity is not in question.
      //
      // isGpcEnabled is strict === true, so a truthy string can never opt
      // someone out by coercion and an absent property is never read as a
      // signal. It is the same predicate the pixel wrapper and the attribution
      // builder use, so the three cannot drift apart.
      const gpc = isGpcEnabled();

      // `oppref`, the ChatGPT Ads click id, rides the SIGNUP request too. The
      // server uploads its registration_completed conversion during this
      // request, before the post-signup attribution POST that stores the click
      // id can run, so this body is the only way that upload can carry the
      // click (services/payment/src/routes/signup.ts, board
      // otVnzdhn4IW5tyPDB6r6). getAllStoredClickIds rather than
      // getStoredClickIds, so the pixel's own __oppref cookie counts too.
      // Left out under GPC, and that omission is LOAD-BEARING, not tidiness:
      // if the in-transaction GPC savepoint UPDATE fails, the server's
      // checkAdsOptOut reads 'allowed' and the event uploads, so this is then
      // the only thing keeping the click id off it. Do not remove it. With no
      // click id the key is omitted, not sent as null.
      let oppref: string | undefined;
      try {
        oppref = gpc ? undefined : getAllStoredClickIds().oppref || undefined;
      } catch {
        // A click-id cookie read must never cost the visitor their account.
      }

      const response = await fetch(`${paymentBaseUrl}/pay/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, gpc, ...(oppref ? { oppref } : {}) }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = data.error || 'Signup failed';
        const errorCode = data.code || undefined;

        // Map server error codes to user-friendly messages
        if (errorCode === 'EMAIL_EXISTS') {
          throw { message: 'An account with this email already exists', code: 'auth/email-already-in-use' };
        }
        throw { message: errorMessage, code: errorCode };
      }

      // Verification email is now sent server-side by /pay/signup via Listmonk.
      // No client-side sign-in/sign-out needed.
      return { success: true, data: { user: { id: data.userId, email } } };
    } catch (err: unknown) {
      const error = err as { message: string; code?: string };

      let errorMessage = error.message;
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists';
      } else if (error.code === 'auth/weak-password' || error.code === 'INVALID_PASSWORD') {
        errorMessage = 'Password should be at least 6 characters';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      }

      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Sign out with cleanup
  const handleSignOut = useCallback(async (): Promise<void> => {
    authLog.info('Sign out initiated');
    setLoading(true);
    try {
      // Clear legacy auth storage
      clearAuthStorage();
      sessionStorage.removeItem('signup-confirmation-email');

      // Sign out from Firebase
      await firebaseSignOut(firebaseAuth);

      // Clear persistence tracking
      previousUserId.current = null;

      // Clear local state
      setUser(null);
      setSession(null);

      // Clear React Query cache
      queryClient.clear();

      authLog.info('Sign out completed successfully');

      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    } catch (err) {
      logger.error('Error signing out:', err);
      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const contextValue: AuthContextType = {
    user,
    session,
    signIn,
    signUp,
    signOut: handleSignOut,
    loading,
    isAuthResolved,
    isImpersonating,
    impersonatedBy
  };

  return (
    <AuthContext value={contextValue}>
      {children}
    </AuthContext>
  );
}

// Custom hook to use the auth context
export function useAuth() {
  return useContext(AuthContext);
}

// Utility function to clear all auth-related storage
export function clearAuthStorage() {
  try {
    // Clear legacy Supabase auth storage keys
    const keysToRemove = [
      'ask-jds-auth-storage',
      'sb-prbbuxgirnecbkpdpgcb-auth-token',
      'sb-prbbuxgirnecbkpdpgcb-auth-token-code-verifier',
      'supabase.auth.token',
      'last_token_validation',
      'last_validation_result',
    ];

    // Post-auth intent (src/lib/auth-intent.ts). NOT optional hygiene: a stored
    // `start_bar_trial` intent that outlives a sign-out is executed by WHOEVER
    // SIGNS IN NEXT on this device, and a bar_package trial is
    // once-per-user-forever (services/payment/src/routes/trial.ts:77-91) — so
    // leaving it behind on a shared machine spends a second person's only free
    // trial on an ad click they never made. Called rather than added to the
    // array above so the storage key has exactly one owner.
    clearPostAuthIntent();

    // Last-visited page (src/lib/last-visited-page.ts) and any pending study
    // group invite (src/lib/pending-invite.ts). BELT-AND-BRACES, not the
    // primary guard: since board sYIAm69jLziJCcJifsMO the last-visited value
    // carries the uid it was written for and a foreign uid is rejected AND
    // cleared on read, so a leak across accounts cannot happen through the read
    // path. This removes the value at the earliest honest moment anyway, so
    // that a future writer who forgets the uid argument cannot resurrect the
    // shared-computer leak on its own. Same one-owner-per-key discipline as the
    // intent above — called, not added to the array.
    clearLastVisitedPage();
    clearPendingInvite();

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    // Clear any Supabase IndexedDB data
    if ('indexedDB' in window) {
      indexedDB.databases().then(databases => {
        databases.forEach(db => {
          if (db.name?.includes('supabase')) {
            indexedDB.deleteDatabase(db.name);
          }
        });
      });
    }

    logger.debug('Auth storage cleared successfully');
    return true;
  } catch (error) {
    logger.error('Error clearing auth storage:', error);
    return false;
  }
}

// Function to validate session token
export async function validateSessionToken(): Promise<boolean> {
  try {
    const user = firebaseAuth.currentUser;
    if (!user) return false;

    // Firebase's getIdToken auto-refreshes expired tokens.
    // Force refresh to validate the token is still valid server-side.
    await user.getIdToken(true);
    return true;
  } catch (err) {
    logger.error('Error validating session token:', err);
    return false;
  }
}

// Function to handle session expiration and preserve user data
export const handleSessionExpiration = (messageContent?: string) => {
  logger.debug('[DEBUG] Session expired, preserving message and redirecting');
  if (messageContent && messageContent.trim()) {
    localStorage.setItem('preservedMessage', messageContent);
    const threadId = localStorage.getItem('currentThreadId');
    if (threadId) localStorage.setItem('preservedThreadId', threadId);
  }
  if (typeof window !== 'undefined') {
    window.location.href = '/auth';
  }
};
